import {
  Button,
  CloseButton,
  SettingsButton,
  SaveButton,
} from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import {
  DateRangeTypeOption,
  IDateRange,
  IInclusiveDateOption,
} from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  comparisonReportSettingsProvider,
  IComparisonReportSettings,
  IComparisonReportSettingsState,
} from '../../../../data/providers/settings/reports/comparisonReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { isValidComparisonPopulation } from '../../../../data/providers/utils/isValidComparisonPopulation';
import { isValidDateRange } from '../../../../data/providers/utils/isValidDateRange';
import { IRange, ISelectOption } from '../../../../data/types';
import {
  deleteSelectedTemplate,
  saveComparisonSettings,
} from '../../../../data/writers/settings/reports/comparisonReportSettings';
import { Row } from '../../../layout/Row';
import { ComparisonSettings } from '../../../settings/comparison/ComparisonSettings';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { DateSelect } from '../../../shared/form/selects/DateRangeSelect';
import { Step } from '../../../shared/form/Step';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TemplateForm } from './TemplateForm';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<IComparisonReportSettings>>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<IComparisonReportSettings>>
  >([]);
  const [initialTemplate, setInitialTemplate] =
    React.useState<ISettingsTemplate<IComparisonReportSettings>>();
  const [targetTimeframe, setTargetTimeframe] = React.useState<IDateRange>();
  const [comparisonPopulation, setComparisonPopulation] =
    React.useState<IComparisonReportSettingsState['comparison']>();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [saving, setSaving] = React.useState<boolean>(false);

  /**
   * @description subscribe to trend report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = comparisonReportSettingsProvider().subscribe(
      (mSettings: IComparisonReportSettingsState): void => {
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialTemplate(mSettings?.selected);
        /// make sure there are values before overriding local state
        if (!isEmpty(mSettings?.comparison)) {
          setComparisonPopulation(mSettings?.comparison);
        }
        if (!isEmpty(mSettings?.targetTimeframe)) {
          setTargetTimeframe(mSettings?.targetTimeframe);
        }

        setLoading(false);
      }
    );

    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setSaving(true);
    if (!isEqual(initialTemplate?.id, selectedTemplate?.id)) {
      props.onSave();
    }
    await saveComparisonSettings(
      selectedTemplate,
      targetTimeframe,
      comparisonPopulation
    );
    setSaving(false);
    props.onClose();
  };

  /**
   * @description handle changes to the date range
   * @param dateType
   * @param dateRange
   */
  const targetTimeframeChangeHandler = (
    dateType: DateRangeTypeOption,
    dateRange: IRange
  ): void => {
    setTargetTimeframe({
      dateType: dateType as IInclusiveDateOption,
      dateRange,
    });
  };

  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<IComparisonReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<IComparisonReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<IComparisonReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<IComparisonReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  /**
   * @description handles change to the comparsion population entity
   * @param entity
   */
  const handleComparisonEntityChange = (entity: ISelectOption): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      entity,
    });
  };

  /**
   * @description handles date change
   */

  const handleComparisonDateChange = (
    dateRangeType: IInclusiveDateOption,
    dateRangeValues: IRange
  ): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      dateRangeType,
      dateRangeValues,
    });
  };

  /**
   * @description determines if the setting is valid and is able to be saved
   */
  const isValid = React.useMemo((): boolean => {
    /// make sure user has selected a valid timeframe
    if (!isValidDateRange(targetTimeframe)) return false;
    /// make sure the user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// make sure the user has selected a comparison population
    if (!isValidComparisonPopulation(comparisonPopulation, true)) return false;
    /// form is valid and can be submitted
    return true;
  }, [
    JSON.stringify(targetTimeframe),
    JSON.stringify(comparisonPopulation),
    JSON.stringify(selectedTemplate),
  ]);

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Comparison Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {(loading || saving) && <LinearLoading />}
          <Row shouldCollapse>
            <LeftColumn>
              <TemplateSelect
                reportType={'Comparison Report'}
                step={1}
                templates={templates}
                onSelect={templateChangeHandler}
                onDelete={templateDeleteHandler}
                selectedTemplateId={selectedTemplate?.id}
                formComponent={TemplateForm}
                reportTypeId={'comparisonReport'}
              />
            </LeftColumn>
            <RightColumn>
              <FormSection>
                <CardHeader>
                  <CardHeaderLeft>
                    <Step>2</Step>
                    <h6>Select Date Range</h6>
                  </CardHeaderLeft>
                </CardHeader>
                <div style={{ padding: '10px' }}>
                  <ControlSpacer>
                    <DateSelect
                      type='inclusive'
                      dateRangeType={targetTimeframe?.dateType}
                      dateRange={targetTimeframe?.dateRange}
                      onChange={targetTimeframeChangeHandler}
                    />
                  </ControlSpacer>
                </div>
              </FormSection>
              <FormSection>
                <CardHeader>
                  <CardHeaderLeft>
                    <Step>3</Step>
                    <h6>Select Comparison</h6>
                  </CardHeaderLeft>
                </CardHeader>
                <ComparisonSettings
                  zscoreDisabled
                  inclusiveDates
                  handleEntityChange={handleComparisonEntityChange}
                  handleDateChange={handleComparisonDateChange}
                  settings={comparisonPopulation}
                />
              </FormSection>
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton
              onClick={handleSave}
              disabled={loading || saving || !isValid}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const ComparisonReportSettings = withRouter(
  (props: IProps): React.ReactElement => {
    const open = (): void =>
      openPopup(
        <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
        props.history,
        'comparison-report-settings'
      );

    return <SettingsButton onClick={open} style={props.style} />;
  }
);
